import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { produce } from 'immer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { SaveOutline } from '../../../../../../../icons/icons';
import {
    AircraftMonitorSettingsMerger,
    AircraftThresholdMergeResult
} from '../../../../../../../monitoring/merger/aircraft-threshold-merger';
import { defaultAircraftThresholds } from '../../../../../../../monitoring/monitor-defaults';
import {
    AircraftThresholdType,
    CompanyThresholdsMode,
    MonitorAircraftThreshold,
    MonitorAircraftThresholdAlerts,
    ThresholdEnableState,
    ThresholdLevel
} from '../../../../../../../types/thresholds';
import { LoadingIndicatorCircular } from '../../../../../../components/indicators/LoadingIndicatorCircular';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store-hooks';
import { updateMonitorSettings } from '../../../../../../state/slices/global-slice';
import { store } from '../../../../../../state/store';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../../context/SettingsNavContext';
import { AircraftAlertEditView } from '../common/AlertEditView';
import { AircraftSettingsEditThresholdLevel } from '../common/SettingsEditThresholdLevel';
import { ThresholdHeaderBar } from '../common/ThresholdHeaderBar';
import { ThresholdsEditPopup } from '../common/ThresholdsEditPopup';
import { AircraftThresholdAlertClickedArgs, AircraftThresholdLevelClickedArgs } from '../common/ThresholdsGirdRow';
import { ThresholdsGrid } from '../common/ThresholdsGrid';

function useAircraftThresholdsEditContext() {
    const displayOrder = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.displayOrder);
    const thresholdDefinitions = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.definitions);
    const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);
    const merger = React.useMemo(() => new AircraftMonitorSettingsMerger(), []);
    const [edited, setEdited] = React.useState(false);
    const [tab, setTab] = React.useState<'user' | 'company'>('user');
    const [companyMode, setCompanyMode] = useState(() => store.getState().global.monitorSettings.company.mode);
    const [displayData, setDisplayData] = React.useState<Record<AircraftThresholdType, AircraftThresholdMergeResult>>();

    useEffect(() => {
        const state = store.getState();
        const settings = {
            user: state.global.monitorSettings.user.aircraft.thresholds,
            company: state.global.monitorSettings.company.aircraft.thresholds
        };
        merger.setMode(companyFeaturesEnabled ? companyMode : CompanyThresholdsMode.notUsed);
        merger.setDefinitions(thresholdDefinitions);
        merger.setData(settings);
        setDisplayData(merger.getThresholdsFor(tab));
    }, [companyFeaturesEnabled, thresholdDefinitions]);

    const updateRow = useCallback(
        (type: AircraftThresholdType, recipe: (draft: MonitorAircraftThreshold) => void) => {
            const data = merger.getData();
            const threshold = data[tab].find((x) => x.type === type) ?? newAircraftThreshold(type);
            const edited = produce(threshold, (r) => {
                // Make a wrapper function in case someone passed a function that returns a value, which immer doesn't like
                recipe(r);
            });

            const editedRow = merger.updateRow(tab, edited);
            setDisplayData((p) => ({ ...p!, [type]: editedRow }));
            setEdited(true);
        },
        [merger, tab]
    );

    const updateCompanyMode = useCallback(
        (mode: CompanyThresholdsMode) => {
            setCompanyMode(mode);
            merger.setMode(mode);
            setDisplayData(merger.getThresholdsFor(tab));
            setEdited(true);
        },
        [merger, tab]
    );

    const changeTab = useCallback(
        (tab: 'user' | 'company') => {
            setTab(tab);
            setDisplayData(merger.getThresholdsFor(tab));
        },
        [merger]
    );

    const setDefaults = useCallback(() => {
        const data = merger.getData();
        merger.setData({
            ...data,
            [tab]: defaultAircraftThresholds
        });
        setDisplayData(merger.getThresholdsFor(tab));
        setEdited(true);
    }, [tab, merger]);

    const clearAll = useCallback(() => {
        const data = merger.getData();
        merger.setData({
            ...data,
            [tab]: []
        });

        setDisplayData(merger.getThresholdsFor(tab));
        setEdited(true);
    }, [tab, merger]);

    const clearEdited = useCallback(() => {
        setEdited(false);
    }, []);

    return {
        tab,
        companyMode,
        displayData,
        edited,
        displayOrder,
        thresholdDefinitions,
        clearEdited,
        changeTab,
        updateRow,
        updateCompanyMode,
        setDefaults,
        clearAll,
        getData: merger.getData.bind(merger)
    };
}

export default function SettingsAircraftThresholds() {
    useSettingsPageWidth('100%');
    const canEditCompanyThresholds = useAppSelector((x) => x.user.rights.canEditCompanyThresholds);
    const [busy, setBusy] = React.useState(false);
    const [editLevel, setEditLevel] = useState<AircraftThresholdLevelClickedArgs | null>(null);
    const [editAlert, setEditAlert] = useState<AircraftThresholdAlertClickedArgs | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const {
        edited,
        tab,
        displayData: data,
        companyMode,
        displayOrder,
        thresholdDefinitions,
        clearEdited,
        updateRow,
        updateCompanyMode,
        changeTab,
        setDefaults,
        clearAll,
        getData
    } = useAircraftThresholdsEditContext();
    const blocker = useBlocker(edited);

    const handleEnableChanged = useCallback(
        (type: AircraftThresholdType, enableState: ThresholdEnableState) => {
            updateRow(type, (x) => (x.enableState = enableState));
        },
        [updateRow]
    );

    const handlePriorityChanged = useCallback(
        (type: AircraftThresholdType, priority: number) => {
            updateRow(type, (x) => (x.priority = priority));
        },
        [updateRow]
    );

    const handleEditThresholdLevel = useCallback(
        (type: AircraftThresholdType, level: ThresholdLevel, recipe: (draft: MonitorAircraftThreshold) => void) => {
            updateRow(type, recipe);
        },
        [updateRow]
    );

    const handleLevelClicked = useCallback((e: AircraftThresholdLevelClickedArgs) => {
        setEditLevel(e);
        setEditAlert(null);
    }, []);

    const handleAlertsClicked = useCallback((e: AircraftThresholdAlertClickedArgs) => {
        setEditLevel(null);
        setEditAlert(e);
    }, []);

    const handleSave = useCallback(async () => {
        const data = getData();
        setBusy(true);
        try {
            const result = await dispatch(
                updateMonitorSettings({
                    user: { aircraft: { thresholds: data.user } },
                    company: canEditCompanyThresholds ? { aircraft: { thresholds: data.company }, companyMode } : undefined
                })
            ).unwrap();

            if (result) {
                clearEdited();
                return true;
            }
        } catch {
            // Failed
        } finally {
            setBusy(false);
        }

        return false;
    }, [getData, companyMode, canEditCompanyThresholds]);

    const handleSaveConfirm = async () => {
        if (await handleSave()) {
            if (blocker.state === 'blocked') {
                blocker.proceed();
            }
        } else {
            if (blocker.state === 'blocked') {
                blocker.reset();
            }
        }
    };

    return (
        <div className="pb-2">
            <div className="k-card overflow-x-auto mb-2 h-100" ref={containerRef}>
                {blocker.state === 'blocked' && (
                    <Dialog title="Aircraft thresholds" onClose={() => blocker.reset()}>
                        <div>Do you want to save pending changes?</div>
                        <DialogActionsBar>
                            <Button themeColor={'primary'} onClick={handleSaveConfirm} disabled={busy}>
                                Yes
                            </Button>
                            <Button fillMode={'outline'} onClick={() => blocker.proceed()} disabled={busy}>
                                No
                            </Button>
                        </DialogActionsBar>
                    </Dialog>
                )}
                <SettingsNavTitle>
                    <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">Aircraft thresholds & alerts</div>
                        {busy && <LoadingIndicatorCircular size="1.5rem" />}
                        <Button disabled={!edited} svgIcon={SaveOutline} onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                </SettingsNavTitle>

                <ThresholdHeaderBar
                    tab={tab}
                    companyMode={companyMode}
                    onChangeCompanyMode={updateCompanyMode}
                    onChangeTab={changeTab}
                    onRestoreDefaultClicked={setDefaults}
                    onClearClicked={clearAll}
                />

                {data && editLevel && (
                    <ThresholdsEditPopup
                        reference={editLevel.element}
                        boundary={containerRef.current ?? undefined}
                        onClose={() => setEditLevel(null)}>
                        <AircraftSettingsEditThresholdLevel
                            kind="aircraft"
                            mode={companyMode}
                            type={editLevel.type}
                            info={thresholdDefinitions[editLevel.type]}
                            level={editLevel.level}
                            threshold={data[editLevel.type]}
                            onEdit={handleEditThresholdLevel}
                        />
                    </ThresholdsEditPopup>
                )}

                {data && editAlert && (
                    <ThresholdsEditPopup
                        placement="left"
                        reference={editAlert.element}
                        boundary={containerRef.current ?? undefined}
                        onClose={() => setEditAlert(null)}>
                        <AircraftAlertEditView
                            tab={tab}
                            type={editAlert.type}
                            threshold={data[editAlert.type]}
                            info={thresholdDefinitions[editAlert.type]}
                            mode={companyMode}
                            onEdit={updateRow}
                        />
                    </ThresholdsEditPopup>
                )}

                {data && (
                    <ThresholdsGrid
                        type="aircraft"
                        displayOrder={displayOrder}
                        readonly={tab === 'company' && !canEditCompanyThresholds}
                        data={data}
                        companyMode={companyMode}
                        thresholdDefinitions={thresholdDefinitions}
                        onEnableChange={handleEnableChanged}
                        onPriorityChange={handlePriorityChanged}
                        onLevelClicked={handleLevelClicked}
                        onAlertsClicked={handleAlertsClicked}
                    />
                )}
            </div>
        </div>
    );
}

function newAircraftThreshold(type: AircraftThresholdType): MonitorAircraftThreshold {
    return {
        type: type,
        priority: 50,
        enableState: ThresholdEnableState.notSet,
        info: null,
        watch: null,
        warning: null,
        critical: null,
        emergency: null,
        alerts: {
            eventList: { value: null, minLevel: null },
            eventListAck: { value: null, minLevel: null },
            eventListImportant: { value: null, minLevel: null },
            email: { value: null, minLevel: null },
            logFile: { value: null, minLevel: null },
            popup: { value: null, minLevel: null },
            osAlert: { value: null, minLevel: null },
            aircraftTag: { value: null, minLevel: null },
            zoomToAircraft: { value: null, minLevel: null },
            overrideColor: { value: null, minLevel: null },
            enableMarker: { value: null, minLevel: null },
            voice: { value: null, minLevel: null },
            playSound: { value: null, minLevel: null },
            overrideColorHex: null,
            alertOnScheduled: { value: null, minLevel: null },
            alertOnPlanned: { value: null, minLevel: null },
            alertOnActive: { value: null, minLevel: null }
        } satisfies MonitorAircraftThresholdAlerts,
        ignoredInheritedLevels: []
    } satisfies MonitorAircraftThreshold;
}
